import { useState } from 'react';
import apiClient from '../services/apiClientService';
import { Routes } from '../routes/RouteConstants';
import useErrorHandler, { isBookingGone, isBookingStateChanged, isCancellationTooLate } from '../hooks/useErrorHandler';
import { useTenant } from '../contexts/TenantContext';

/**
 * The guest cancels their own booking from the profile: DELETE …/cancel. The admin's delete from
 * the dashboard is useDeleteBooking, a different endpoint with different rules.
 *
 * What a cancellation came to. GONE: the booking had been removed already (DEC-0023) — the
 * snackbar has said so, and the page should drop it like a cancelled one. TOO_LATE: the
 * cancellation deadline passed while the page was open (DEC-0024) — the booking stays, and the
 * page should reload it to show that it can no longer be cancelled. CHANGED: the booking closed
 * while the page was open (DEC-0026) — rejected, or its time passed — and the page should reload
 * it the same way.
 */
export const CancelOutcome = Object.freeze({
  CANCELLED: 'CANCELLED',
  GONE: 'GONE',
  TOO_LATE: 'TOO_LATE',
  CHANGED: 'CHANGED',
  FAILED: 'FAILED',
});

const useCancelBooking = () => {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const { handleError } = useErrorHandler();

  const cancelBooking = async (bookingId) => {
    setLoading(true);

    try {
      const url = `/v1/tenants/${tenantId}/bookings/${bookingId}/cancel`;
      await apiClient.delete(url);
      return CancelOutcome.CANCELLED;
    } catch (error) {
      handleError(error, Routes.PROFILE);
      if (isBookingGone(error)) return CancelOutcome.GONE;
      if (isCancellationTooLate(error)) return CancelOutcome.TOO_LATE;
      if (isBookingStateChanged(error)) return CancelOutcome.CHANGED;
      return CancelOutcome.FAILED;
    } finally {
      setLoading(false);
    }
  };

  return { cancelBooking, loading };
};

export default useCancelBooking;
